import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-enterprise";
import {
  BooleanYNBadgeRenderer,
  LinkCellRenderer,
  ManualIconCellRenderer,
} from "../../../../controls/ag-grid/components/ButtonCellRenderer";
import { CommentsColumn } from "@/controls/ag-grid/components/CommentsColumn";

type Props = {
  showCommentModal: boolean;
  setShowCommentModal: (
    show: boolean,
    data?: any,
    position?: { top: number; left: number },
  ) => void;
};

export const useLMFTableData = ({ setShowCommentModal }: Props) => {
  const columns: (ColDef | ColGroupDef)[] = [
    {
      field: "HeatName",
      headerName: "Heat",
      cellRenderer: (params: ICellRendererParams) => {
        return LinkCellRenderer({
          ...params,
          link: `/meltshop-heats/${params.data.HeatShiftId}?heatName=${params.data.HeatName}`,
        });
      },
      flex: 1,
      minWidth: 60,
    },
    {
      field: "TreatmentNumber",
      headerName: "Treat No.",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 65,
    },
    {
      field: "GradeName",
      headerName: "Grade",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 80,
    },
    {
      field: "LadleName",
      headerName: "Ladle",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 60,
    },
    {
      field: "Crew",
      headerName: "Crew",
      flex: 1,
      minWidth: 40,
    },
    {
      field: "LadleCar",
      headerName: "Ladle Car",
      type: ["numericColumn", "numericWidth"],
      wrapHeaderText: true,
      autoHeaderHeight: true,
      flex: 1,
      minWidth: 60,
    },
    {
      headerName: "Times",
      children: [
        {
          field: "Arrive",
          headerName: "Arrive",
          flex: 1,
          minWidth: 60,
          cellRenderer: (params: ICellRendererParams) => {
            return LinkCellRenderer({
              ...params,
              link: `/lmf-data-entry/${params.data.ProcessId}`,
            });
          },
          valueGetter: (params) =>
            dxtToLocalServerTime(params.data?.Arrive, "HH:mm:ss") || "",
        },
        {
          field: "Ship",
          headerName: "Ship",
          flex: 1,
          minWidth: 60,
          valueGetter: (params) =>
            dxtToLocalServerTime(params.data?.Ship, "HH:mm:ss") || "",
        },
        {
          field: "ProcessTime",
          headerName: "Duration",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 75,
          valueGetter: (params) => params.data?.ProcessTime?.toFixed(1) || "",
        },
        {
          field: "PowerOn",
          headerName: "On",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.PowerOn?.toFixed(1) || 0,
        },
        {
          field: "Lance",
          headerName: "Lance",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 60,
          valueGetter: (params) => params.data?.Lance?.toFixed(1) || 0,
        },
      ],
    } as ColGroupDef,
    {
      field: "KWHPerTon",
      headerName: "KWH",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 60,
      valueGetter: (params) => params.data?.KWHPerTon?.toFixed(1) || 0,
    },
    {
      headerName: "InPlug",
      children: [
        {
          field: "PlugInsideTime",
          headerName: "Time",
          type: ["numericColumn", "numericWidth"],
          minWidth: 60,
          valueGetter: (params) => params.data?.PlugInsideTime?.toFixed(1) || 0,
        },
        {
          field: "PlugInsideVol",
          headerName: "Vol",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 60,
          valueGetter: (params) => params.data?.PlugInsideVol?.toFixed(1) || 0,
        },
      ],
    } as ColGroupDef,
    {
      headerName: "OutPlug",
      children: [
        {
          field: "PlugOutsideTime",
          headerName: "Time",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 60,
          valueGetter: (params) =>
            params.data?.PlugOutsideTime?.toFixed(1) || 0,
        },
        {
          field: "PlugOutsideVol",
          headerName: "Vol",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 60,
          valueGetter: (params) => params.data?.PlugOutsideVol?.toFixed(1) || 0,
        },
      ],
    } as ColGroupDef,
    {
      field: "Heatings",
      headerName: "Heatings",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 70,
      valueGetter: (params) => params.data?.Heatings || 0,
    },
    {
      headerName: "Additions",
      children: [
        {
          field: "LimeWeight",
          headerName: "Lime",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
        },
        {
          field: "SiMnWeight",
          headerName: "SiMn",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
        },
        {
          field: "CrWeight",
          headerName: "Cr",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
        },
        {
          field: "FeSiWeight",
          headerName: "FeSi",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
        },
      ],
    } as ColGroupDef,
    {
      field: "EAFSlaggedInLadle",
      headerName: "EAF Slagged",
      type: ["nonSorteable", "nonFilterable", "centerAligned"],
      cellRenderer: BooleanYNBadgeRenderer,
      cellClass: "ag-button-cell",
      wrapHeaderText: true,
      autoHeaderHeight: true,
      flex: 1,
      minWidth: 70,
    },
    {
      field: "Comments",
      headerName: "Notes",
      flex: 1,
      minWidth: 60,
      cellRenderer: (params: ICellRendererParams) => {
        return CommentsColumn({
          data: params?.data,
          setShowOpenModal: setShowCommentModal,
        });
      },
      valueGetter: (params) => params.data?.Comments || "",
    },
    {
      field: "IsManual",
      headerName: "",
      type: ["nonSorteable", "nonFilterable", "centerAligned"],
      cellRenderer: ManualIconCellRenderer,
      cellClass: "ag-button-cell",
      flex: 1,
      minWidth: 60,
    },
  ];

  return { columns };
};
